import { Box, Button, Popover, Typography, Divider, FormControlLabel, Checkbox, Tooltip } from "@mui/material";
import { downloadExcel, downloadCSV, downloadPNG, downloadFormattedExcel, downloadFormattedCSV } from "../../utils/download";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import ImageIcon from "@mui/icons-material/Image";
import TableViewIcon from "@mui/icons-material/TableView";
import { forwardRef, useState } from "react";
import { useTheme } from "@emotion/react";

const DownloadButtons = forwardRef((props, ref) => {
    const { originalData, title, country, params, data, currentTab = 0 } = props;

    const lastYear = params?.year.slice(-1).pop();
    const firstYear = params?.year[0];
    const theme = useTheme();

    const [anchorEl, setAnchorEl] = useState(null);
    const [includeTitle, setIncludeTitle] = useState(true);
    const [useFormatted, setUseFormatted] = useState(false);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "chart" : undefined;

    return (
        <>
            <Box sx={{ mr: "-13px!important" }}>
                <Button
                    aria-label="download data"
                    sx={{
                        color: theme.palette.buttonBase.main,
                        textTransform: "none",
                        mt: 0.2,
                    }}
                    onClick={handleClick}
                    id="ico-download"
                    startIcon={
                        <DownloadForOfflineIcon sx={{ fontSize: "30px!important" }} />
                    }
                >
                    <Box
                        component="span"
                        sx={{ display: { xs: "flex", sm: "none", fontSize: "smaller" } }}
                    >
                        Download
                    </Box>
                </Button>
            </Box>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Box
                    display="flex"
                    className="download-area"
                    flexDirection="column"
                    sx={{ p: 2, minWidth: 280 }}
                >
                    {/* Chart Download Section */}
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <ImageIcon sx={{ mr: 1, fontSize: 18, color: theme.palette.primary.main }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Chart Download
                            </Typography>
                        </Box>
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={includeTitle}
                                    onChange={(e) => setIncludeTitle(e.target.checked)}
                                    disabled={currentTab === 1}
                                />
                            }
                            label={<Typography variant="body2">Include title</Typography>}
                            sx={{ ml: 0.5, mb: 1 }}
                        />
                        
                        <Tooltip
                            title={currentTab === 1 ? "Switch to Chart tab to download chart image" : ""}
                            placement="top"
                            arrow
                        >
                            <span style={{ width: '100%' }}>
                                <Button
                                    fullWidth
                                    size="small"
                                    variant="contained"
                                    disabled={currentTab === 1}
                                    onClick={() => {
                                        downloadPNG(
                                            ref,
                                            title,
                                            country,
                                            firstYear,
                                            lastYear,
                                            theme.palette.secondary.main,
                                            includeTitle
                                        );
                                        handleClose();
                                    }}
                                    sx={{ justifyContent: 'flex-start' }}
                                >
                                    Download as PNG
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Data Download Section */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TableViewIcon sx={{ mr: 1, fontSize: 18, color: theme.palette.primary.main }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Data Download
                            </Typography>
                        </Box>
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={useFormatted}
                                    onChange={(e) => setUseFormatted(e.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    Use table formatting
                                </Typography>
                            }
                            sx={{ ml: 0.5, mb: 1 }}
                        />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                    if (useFormatted) {
                                        downloadFormattedExcel(data, originalData, title, country, firstYear, lastYear);
                                    } else {
                                        downloadExcel(originalData, title, country, firstYear, lastYear);
                                    }
                                    handleClose();
                                }}
                                sx={{ justifyContent: 'flex-start' }}
                            >
                                Download Excel
                            </Button>
                            <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                    if (useFormatted) {
                                        downloadFormattedCSV(data, originalData, new Date(), title, country, firstYear, lastYear);
                                    } else {
                                        downloadCSV(
                                            new Date(),
                                            originalData,
                                            title,
                                            country,
                                            firstYear,
                                            lastYear
                                        );
                                    }
                                    handleClose();
                                }}
                                sx={{ justifyContent: 'flex-start' }}
                            >
                                Download CSV
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Popover>
        </>
    );
});

export default DownloadButtons;
